import { GpxRouteApi } from './GpxRouteApi';
import { IRouteMetadata } from './GpxEditCommands';
import { DEFAULT_ROUTE_COLOUR } from './GpxEditStyles';
import { GpxTagEditor } from './GpxTagEditor';

export type MetadataDialogResult =
    | { action: 'save'; values: IRouteMetadata }
    | { action: 'cancel' };

/**
 * The in-map route details dialog (#gpxMetadataDialog): name, notes, colour
 * and a tag chip editor with autocomplete from the user's existing tags.
 */
export class GpxMetadataDialog {

    private dialog: HTMLElement;
    private resolve: ((result: MetadataDialogResult) => void) | null = null;
    private tagEditor: GpxTagEditor;

    public constructor(private api: GpxRouteApi) {
        this.dialog = document.getElementById('gpxMetadataDialog');

        document.getElementById('gpxMetaSave').addEventListener('click', () => this.handleSave());
        document.getElementById('gpxMetaCancel').addEventListener('click', () => this.close({ action: 'cancel' }));
        document.getElementById('gpxMetaCancelX').addEventListener('click', () => this.close({ action: 'cancel' }));

        this.tagEditor = new GpxTagEditor(this.api, {
            inputId: 'gpxMetaTagInput',
            chipsId: 'gpxMetaTagChips',
            suggestionsId: 'gpxMetaTagSuggestions'
        });
    }

    public show(values: IRouteMetadata, title: string = 'Route details'): Promise<MetadataDialogResult> {
        (<HTMLInputElement>document.getElementById('gpxMetaName')).value = values.displayName ?? '';
        (<HTMLTextAreaElement>document.getElementById('gpxMetaNotes')).value = values.notes ?? '';
        (<HTMLInputElement>document.getElementById('gpxMetaColour')).value = values.colourHex || DEFAULT_ROUTE_COLOUR;
        document.getElementById('gpxMetadataDialogTitle').textContent = title;
        document.getElementById('gpxMetaNameError').classList.add('d-none');

        this.tagEditor.reset(values.tags);

        this.dialog.classList.remove('d-none');
        (<HTMLInputElement>document.getElementById('gpxMetaName')).focus();

        return new Promise<MetadataDialogResult>((resolve) => {
            this.resolve = resolve;
        });
    }

    get isOpen(): boolean {
        return !this.dialog.classList.contains('d-none');
    }

    public hide(): void {
        this.close({ action: 'cancel' });
    }

    private handleSave(): void {
        const tags = this.tagEditor.getTags();

        const name = (<HTMLInputElement>document.getElementById('gpxMetaName')).value.trim();
        if (!name) {
            document.getElementById('gpxMetaNameError').classList.remove('d-none');
            return;
        }

        const notes = (<HTMLTextAreaElement>document.getElementById('gpxMetaNotes')).value.trim();
        const colour = (<HTMLInputElement>document.getElementById('gpxMetaColour')).value;

        this.close({
            action: 'save',
            values: {
                displayName: name,
                notes: notes || null,
                colourHex: colour,
                tags: tags
            }
        });
    }

    private close(result: MetadataDialogResult): void {
        this.dialog.classList.add('d-none');
        const resolve = this.resolve;
        this.resolve = null;
        resolve?.(result);
    }
}
